// oxlint-disable-next-line @atlassian/no-restricted-imports
import type { MemoizedFunction } from 'lodash';
import memoize from 'lodash/memoize';

import EmojiResource, { type EmojiResourceConfig } from '@atlaskit/emoji/emoji-resource';
import type { EmojiLoadFailCallback, EmojiLoadSuccessCallback } from '@atlaskit/emoji/emoji-utils';
import type { EmojiProvider } from '@atlaskit/emoji/types';

type EmojiSSRConfig = Pick<EmojiResourceConfig, 'optimisticImageApi'>;

type ConfigOptions = {
	cloudId?: string | null;
	disableUpload?: boolean;
	emojiSSRConfig?: EmojiSSRConfig;
	onEmojiLoadFail?: EmojiLoadFailCallback;
	onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	userId?: string | null;
};

const BASE_URL =
	process.env.NODE_ENV === 'development'
		? 'https://sdog.jira-dev.com/gateway/api/emoji'
		: '/gateway/api/emoji';

const getEmojiConfig = (
	cloudId?: string,
	userId?: string,
	disableUpload?: boolean,
	emojiSSRConfig?: EmojiSSRConfig,
	emojiLoadCallbacks?: {
		onEmojiLoadFail?: EmojiLoadFailCallback;
		onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	},
): EmojiResourceConfig => {
	const currentUser = userId ? { id: userId } : undefined;

	const providers = [
		{
			url: `${BASE_URL}/standard`,
		},
		{
			url: `${BASE_URL}/atlassian`,
		},
	];

	if (cloudId) {
		providers.push({
			url: `${BASE_URL}/${cloudId}/site`,
		});
	}

	const config: EmojiResourceConfig = {
		providers,
		allowUpload: !!userId && !disableUpload,
		currentUser,
		...(emojiLoadCallbacks || {}),
	};

	if (emojiSSRConfig) {
		return { ...config, ...emojiSSRConfig };
	}

	return config;
};

const resolverOfMemoize = (extraConfig?: ConfigOptions) => {
	const cloudId = extraConfig?.cloudId ?? '';
	const userId = extraConfig?.userId ?? '';
	const disableUpload = extraConfig?.disableUpload ?? false;
	const emojiSSRConfigString = extraConfig?.emojiSSRConfig
		? JSON.stringify(extraConfig.emojiSSRConfig)
		: '';
	return `${cloudId}_${userId}_${disableUpload}_${emojiSSRConfigString}`;
};

const getEmojiResource = memoize((extraConfig?: ConfigOptions): EmojiResource => {
	const cloudId = extraConfig?.cloudId ?? '';
	const userId = extraConfig?.userId ?? undefined;
	const disableUpload = extraConfig?.disableUpload;
	const emojiSSRConfig = extraConfig?.emojiSSRConfig ?? undefined;
	const emojiConfig = getEmojiConfig(cloudId, userId, disableUpload, emojiSSRConfig);

	return new EmojiResource(emojiConfig);
}, resolverOfMemoize);

export const getEmojiProvider: ((extraConfig?: ConfigOptions) => Promise<EmojiProvider>) &
	MemoizedFunction = memoize((extraConfig?: ConfigOptions): Promise<EmojiProvider> => {
	const emojiResource = getEmojiResource(extraConfig);

	return emojiResource.getEmojiProvider();
}, resolverOfMemoize);
